import { createContext, useCallback, useContext, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { authService } from "../services/authService";


const AuthContext = createContext(null);


export function AuthProvider({ children }) {
  const queryClient = useQueryClient();
  const session = useQuery({
    queryKey: ["auth", "me"],
    queryFn: authService.me,
    retry: false,
    staleTime: 60_000,
  });

  const establishSession = useCallback((user) => {
    queryClient.setQueryData(["auth", "me"], user);
    return user;
  }, [queryClient]);

  const updateUser = useCallback((user) => establishSession(user), [establishSession]);

  const login = useCallback(async (email, password) => {
    const data = await authService.login(email, password);
    return establishSession(data.user);
  }, [establishSession]);

  const startRegistration = useCallback((payload) => authService.startRegistration(payload), []);
  const resendRegistration = useCallback((verificationId) => authService.resendRegistration(verificationId), []);
  const verifyRegistration = useCallback(async (verificationId, code) => {
    const data = await authService.verifyRegistration(verificationId, code);
    return establishSession(data.user);
  }, [establishSession]);

  const logout = useCallback(async () => {
    try { await authService.logout(); } finally {
      queryClient.clear();
      window.location.replace("/login");
    }
  }, [queryClient]);

  const value = useMemo(() => ({
    user: session.data || null,
    loading: session.isPending,
    login,
    startRegistration,
    verifyRegistration,
    resendRegistration,
    updateUser,
    logout,
  }), [session.data, session.isPending, login, startRegistration, verifyRegistration, resendRegistration, updateUser, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}


export const useAuth = () => useContext(AuthContext);
